import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Paper,
  Typography,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  TextField,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';

const PROVIDER_LABELS = {
  dropbox: 'Dropbox',
  gdrive: 'Google Drive',
  gcs: 'Google Cloud Storage',
  'aws-s3': 'AWS S3',
  sftp: 'SFTP/SCP',
};

/**
 * Client-side mirror of the server's templateEscapesRoot guard: reject an
 * absolute sub-path or one that climbs above its root. The server re-validates.
 */
function customPathEscapesRoot(template) {
  if (!template) return false;
  if (template.startsWith('/') || template.startsWith('\\')) return true;
  let depth = 0;
  for (const seg of template.split(/[\\/]+/)) {
    if (seg === '' || seg === '.') continue;
    if (seg === '..') {
      depth -= 1;
      if (depth < 0) return true;
    } else {
      depth += 1;
    }
  }
  return false;
}

/**
 * On-demand "Push Externally" dialog. Lets the user push an observation's (or a
 * whole project's) captured files to one or more storage destinations they are
 * eligible to use — personal, organization (incl. parent orgs), or project —
 * optionally under a custom sub-path layered on the destination's configured path.
 *
 * Provide exactly one of observationId / projectId.
 */
const PushExternallyDialog = ({ open, onClose, observationId, projectId, projectName, target, date, onQueued }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [customPath, setCustomPath] = useState('');
  const [content, setContent] = useState('raw'); // frames to send: raw | calibrated | both
  const [contentTouched, setContentTouched] = useState(false); // user overrode the prefill
  const [notifySummary, setNotifySummary] = useState(false);
  const [notifyComplete, setNotifyComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getAvailableStorageConfigs();
      if (response?.success) {
        const all = [];
        for (const cfg of response.data?.personal || []) {
          all.push({ ...cfg, _scope: 'personal', _scopeLabel: 'Personal' });
        }
        for (const [orgId, group] of Object.entries(response.data?.organization || {})) {
          for (const cfg of group.configs || []) {
            all.push({ ...cfg, _scope: 'organization', _scopeLabel: group.name, _scopeId: orgId });
          }
        }
        for (const [projId, group] of Object.entries(response.data?.project || {})) {
          for (const cfg of group.configs || []) {
            all.push({ ...cfg, _scope: 'project', _scopeLabel: group.name, _scopeId: projId });
          }
        }
        setConfigs(all);
      }
    } catch (err) {
      setError('Failed to load storage destinations');
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset + load whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    setSelectedIds([]);
    setCustomPath('');
    setContent('raw');
    setContentTouched(false);
    setNotifySummary(false);
    setNotifyComplete(false);
    setResult(null);
    setError(null);
    loadConfigs();
  }, [open, loadConfigs]);

  const toggle = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // Until the user manually changes the frames selector, keep it in sync with
  // the first selected destination's configured transfer_content (raw/calibrated/both).
  useEffect(() => {
    if (contentTouched) return;
    const first = configs.find((c) => c._id === selectedIds[0]);
    if (first?.transfer_content) setContent(first.transfer_content);
  }, [selectedIds, configs, contentTouched]);

  const grouped = useMemo(() => {
    const groups = {};
    for (const cfg of configs) {
      const key =
        cfg._scope === 'personal'
          ? 'Personal'
          : `${cfg._scope === 'organization' ? 'Org' : 'Project'}: ${cfg._scopeLabel}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(cfg);
    }
    return groups;
  }, [configs]);

  const pathEscapes = customPathEscapesRoot(customPath.trim());
  const canSubmit = selectedIds.length > 0 && !pathEscapes && !submitting;

  const resolvedPreview = (cfg) => {
    const root = cfg.destination_path || '';
    const template = cfg.custom_path || '';
    const sub = customPath.trim();
    const resolveTokens = (t) =>
      t
        .replace(/\$USER/g, user?.username || user?.first_name || 'user')
        .replace(/\$PROJECT/g, projectName || 'project')
        .replace(/\$TARGET/g, '<target>');
    let path = root;
    if (template) path = path ? `${path}/${resolveTokens(template)}` : resolveTokens(template);
    if (sub && !pathEscapes) path = path ? `${path}/${resolveTokens(sub)}` : resolveTokens(sub);
    return path;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await apiService.pushExternally({
        config_ids: selectedIds,
        custom_path: customPath.trim() || undefined,
        content: content || undefined,
        observation_id: observationId,
        project_id: projectId,
        target,
        date,
        notify_summary: notifySummary,
        notify_on_complete: notifyComplete,
      });
      if (response?.success) {
        setResult(response.data);
        if (onQueued) onQueued(response.data);
      } else {
        setError(response?.error?.message || 'Push failed');
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Push failed');
    } finally {
      setSubmitting(false);
    }
  };

  const scopeLabel = projectId
    ? `all files in ${projectName ? `"${projectName}"` : 'this project'}`
    : target
      ? `all files for target "${target}"`
      : date
        ? `all files from ${date}`
        : 'this observation’s files';

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CloudUploadIcon color="primary" />
        Push Externally
      </DialogTitle>
      <DialogContent dividers>
        {result ? (
          // The server answers 202 as soon as the request is validated and schedules the rows in
          // the background (#1231), so `queued_files` is not known yet — a large push takes far
          // longer to enumerate than any sane request timeout. `accepted` marks that shape.
          <Alert severity={result.accepted || result.queued_files > 0 ? 'success' : 'info'}>
            {result.message
              || (result.accepted
                ? 'Push accepted — scheduling in the background.'
                : `Queued ${result.queued_files} transfer(s)`)}
            {result.cancelled_prior > 0 && ` (superseded ${result.cancelled_prior} prior transfer(s))`}
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Copy {scopeLabel} to one or more external storage destinations. A manual push never
              deletes files from the server.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            ) : configs.length === 0 ? (
              <Alert severity="info">
                You have no external storage destinations. Add one on your Profile, or via a project
                or organization you belong to.
              </Alert>
            ) : (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <FormLabel component="legend" sx={{ mb: 1 }}>Destinations</FormLabel>
                {Object.entries(grouped).map(([groupName, groupConfigs]) => (
                  <Box key={groupName} sx={{ mb: 1.5 }}>
                    {Object.keys(grouped).length > 1 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}
                      >
                        {groupName}
                      </Typography>
                    )}
                    {groupConfigs.map((cfg) => (
                      <FormControlLabel
                        key={cfg._id}
                        control={
                          <Checkbox
                            checked={selectedIds.includes(cfg._id)}
                            onChange={() => toggle(cfg._id)}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">
                              {cfg.display_name}
                              <Chip
                                label={PROVIDER_LABELS[cfg.provider] || cfg.provider}
                                size="small"
                                variant="outlined"
                                sx={{ ml: 1 }}
                              />
                              {cfg._scope !== 'personal' && (
                                <Chip
                                  label={cfg._scope === 'organization' ? 'Org' : 'Project'}
                                  size="small"
                                  color={cfg._scope === 'organization' ? 'info' : 'secondary'}
                                  sx={{ ml: 0.5 }}
                                />
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {resolvedPreview(cfg)}
                            </Typography>
                          </Box>
                        }
                        sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}
                      />
                    ))}
                  </Box>
                ))}
              </Paper>
            )}

            <TextField
              label="Custom sub-path (optional)"
              value={customPath}
              onChange={(e) => setCustomPath(e.target.value)}
              fullWidth
              size="small"
              placeholder="e.g. manual/$TARGET"
              error={pathEscapes}
              helperText={
                pathEscapes
                  ? 'Sub-path cannot be absolute or climb above the destination root'
                  : 'Added under each destination’s configured path. Supports $USER, $PROJECT, $TARGET.'
              }
              sx={{ mb: 2 }}
            />

            <FormControl>
              <FormLabel sx={{ mb: 0.5 }}>Frames to send</FormLabel>
              <RadioGroup
                row
                value={content}
                onChange={(e) => { setContent(e.target.value); setContentTouched(true); }}
              >
                <FormControlLabel value="raw" control={<Radio size="small" />} label="Raw" />
                <FormControlLabel value="calibrated" control={<Radio size="small" />} label="Calibrated" />
                <FormControlLabel value="both" control={<Radio size="small" />} label="Both" />
              </RadioGroup>
              <Typography variant="caption" color="text.secondary">
                Pre-set from the selected destination; change if needed.
              </Typography>
            </FormControl>

            <FormControl component="fieldset" sx={{ mt: 2, display: 'block' }}>
              <FormLabel component="legend" sx={{ mb: 0.5 }}>Email notifications</FormLabel>
              <FormControlLabel
                control={<Checkbox size="small" checked={notifySummary} onChange={(e) => setNotifySummary(e.target.checked)} />}
                label="Email me a summary of this request"
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={notifyComplete} onChange={(e) => setNotifyComplete(e.target.checked)} />}
                label="Email me when transfers complete"
              />
            </FormControl>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!canSubmit}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
          >
            {submitting ? 'Queuing…' : 'Push'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PushExternallyDialog;
